/**
 * @docs docs/features/website-navigation-shell
 * @adr  ADR-0110
 * @rule The two persona cards sit in the footer of every public page (mock parity F2, Gaetan's letter E21 A of
 *       2026-11-04): on Home they keep Home's frozen final ids, attribution and test id; everywhere else they fire the
 *       frame's `website_footer_investor` and `website_footer_founder` under section `footer_persona`.
 */

/*----------------------------------
- FOOTER: THE PERSONA CARDS' CONTRACT
----------------------------------*/

import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import { DATABASE_SEARCH_ACTIONS } from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import type { ContextualSearchVisiblePersonaIntent } from '@uniquedomains/components/contracts/upgrade/catalogs/contextualSearchConversion';
import { LANDING_PERSONAS } from '@/client/catalogs/landing/pageContent';
import { buildWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { toAbsoluteProductUrl } from '@/client/utils/payment/productUrls';
import { encodeRadarFiltersUrl } from '@/common/libs/filters';

/*----------------------------------
- TYPES
----------------------------------*/

/** What a persona card's call to action carries on a given page: the href, the id it fires and where it is filed. */
export type FooterPersonaCta = {
    href: string;
    triggerId: ConversionTriggerId;
    /** `from` in the signup intent (the journeys read it as `source`). */
    attribution: string;
    /** The `section` of every record the click writes. */
    section: 'final_cta' | 'footer_persona';
    testId: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The attribution every control of Home's closing (the persona cards, the sign-in) carries, as shipped. */
export const HOME_FINAL_ATTRIBUTION = 'website_landing_final_cta';

/** Home's two persona cards keep the final persona ids they fired as whole-card anchors (conversion path frozen). */
export const HOME_FINAL_PERSONA_TRIGGER_IDS: Record<ContextualSearchVisiblePersonaIntent, ConversionTriggerId> = {
    investor: 'website_landing_final_investor',
    founder: 'website_landing_final_founder',
};

/** The attribution of the frame's cards off Home: one for every page, the page itself travels as `page`. */
export const FOOTER_PERSONA_ATTRIBUTION = 'website_footer_persona';

/** The frame's two ids off Home, one per persona for every page (`conversionTriggers.ts`, section `footer_persona`). */
export const FOOTER_PERSONA_TRIGGER_IDS: Record<ContextualSearchVisiblePersonaIntent, ConversionTriggerId> = {
    investor: 'website_footer_investor',
    founder: 'website_footer_founder',
};

export const HOME_FOOTER_PERSONA_TEST_ID = 'landing-home-footer-persona-cta';
export const FOOTER_PERSONA_TEST_ID = 'footer-persona-cta';

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * A persona card's signup, as the shipped Home final card built it (conversion path frozen): a signup carrying the
 * chosen persona, whose return is the Product search seeded with the first persona's default prompt. Not
 * `buildLandingPersonaSignupHref`, which has no return: a card signup would land on the bare default destination
 * instead of the seeded search (found by the batch 7 self-review). The inner pages' cards (F2) take the same shape with
 * their own attribution, so a card lands on the same screen wherever it was clicked.
 */
export const buildFooterPersonaHref = (persona: ContextualSearchVisiblePersonaIntent, attribution: string): string =>
    buildWebsiteAuthHref({
        flow: 'cold',
        mode: 'signup',
        persona,
        personaSource: 'chosen',
        restart: true,
        returnTo: toAbsoluteProductUrl(encodeRadarFiltersUrl({ prompt: LANDING_PERSONAS[0].defaultPrompt }, { surface: 'product' })),
        feature: 'databaseAccess',
        action: DATABASE_SEARCH_ACTIONS.unlockDatabase,
        from: attribution,
    });

/** The card's contract on this page: Home's frozen one under `homeClosing`, the frame's everywhere else. */
export const resolveFooterPersonaCta = (persona: ContextualSearchVisiblePersonaIntent, homeClosing: boolean): FooterPersonaCta =>
    homeClosing
        ? {
              href: buildFooterPersonaHref(persona, HOME_FINAL_ATTRIBUTION),
              triggerId: HOME_FINAL_PERSONA_TRIGGER_IDS[persona],
              attribution: HOME_FINAL_ATTRIBUTION,
              section: 'final_cta',
              testId: HOME_FOOTER_PERSONA_TEST_ID,
          }
        : {
              href: buildFooterPersonaHref(persona, FOOTER_PERSONA_ATTRIBUTION),
              triggerId: FOOTER_PERSONA_TRIGGER_IDS[persona],
              attribution: FOOTER_PERSONA_ATTRIBUTION,
              section: 'footer_persona',
              testId: FOOTER_PERSONA_TEST_ID,
          };
